import * as React from 'react';
import { StarRating } from './StarRating';
import { Avatar } from './Avatar';
import { ShareButton } from './ShareButton';
import { useContent, text } from '../../hooks/useContent';
import { BRAND_NAME } from './Wordmark';
import type { Review } from '../../api/reviews';
import { serviceLabel, fmtDate, fmtReplyAgo } from '../../lib/reviewsFormat';

type CompactProps = {
  variant?: 'compact';
  name: string;
  role: string;
  body: string;
  rating: number;
  avatarInitial: string;
};

type DetailedProps = {
  variant: 'detailed';
  review: Review;
};

type Props = CompactProps | DetailedProps;

function CompactCard({ name, role, body, rating, avatarInitial }: Omit<CompactProps, 'variant'>) {
  return (
    <article className="flex flex-col bg-white border border-neutral-900/10 px-6 py-8 w-[320px] max-w-[340px] flex-shrink-0 snap-start">
      <header className="flex items-center gap-4">
        <Avatar initial={avatarInitial} size="sm" tone="primary" />
        <div className="flex flex-col">
          <span className="text-h3 font-semibold text-primary leading-tight">{name}</span>
          <span className="t-subtitle-blue mt-1">{role}</span>
        </div>
      </header>
      <p className="t-body-blue mt-6 mb-6 line-clamp-5">{body}</p>
      <footer className="mt-auto pt-6 flex items-center gap-3 border-t border-primary/15">
        <StarRating value={rating} />
        <span className="t-price">{rating.toFixed(1)}</span>
      </footer>
    </article>
  );
}

function DetailedCard({ review: r }: { review: Review }) {
  const { data: content } = useContent();
  const monogram = r.name
    .replace(/[^A-Za-z]/g, '')
    .slice(0, 1)
    .toUpperCase();
  // px-5 below sm: at 390px the card is only 334px wide, so the 34px desktop
  // inset eats a fifth of every line. Same breakpoint the reply block steps on.
  return (
    <article className="rounded-[var(--radius-card-sm)] border border-primary/15 bg-white px-5 py-6 shadow-[var(--shadow-card)] transition-[border-color,box-shadow] duration-300 hover:border-primary/30 hover:shadow-[var(--shadow-card-hover)] sm:px-[34px] sm:py-7">
      <header className="grid grid-cols-[auto_1fr_auto] items-center gap-[18px]">
        {r.image ? (
          <Avatar src={r.image} alt={r.name} size="md" />
        ) : (
          <Avatar initial={monogram} size="md" tone="cloud" />
        )}
        <div>
          <div className="text-h3 font-semibold leading-tight text-primary">{r.name}</div>
          <div className="mt-1.5 inline-flex items-center gap-2.5 text-micro font-medium uppercase tracking-eyebrow text-primary/50">
            <span>{serviceLabel(r.service)}</span>
          </div>
        </div>
        <div className="text-right text-micro font-medium uppercase leading-relaxed tracking-eyebrow text-primary/50 [font-variant-numeric:tabular-nums]">
          {fmtDate(r.date)}
          <div className="mt-1 text-[10px] tracking-[0.22em] text-primary/60">{r.source}</div>
        </div>
      </header>

      <div className="mt-5 flex items-center gap-3.5">
        <StarRating value={r.rating} />
        <span className="text-small font-semibold text-primary [font-variant-numeric:tabular-nums]">
          {r.rating.toFixed(1)}
        </span>
      </div>

      <h2 className="mt-3.5 text-h3 font-semibold leading-snug text-primary [text-wrap:balance]">
        {r.title}
      </h2>
      <p className="t-body mt-3 !max-w-none text-primary/80">{r.body}</p>

      <footer className="mt-5 border-t border-primary/15 pt-[18px]">
        <ShareButton title={r.title} variant="link" />
      </footer>

      {r.reply ? (
        <div className="relative ml-3 mt-5 border-l-2 border-primary bg-primary/[0.04] px-4 py-[22px] sm:ml-6 sm:px-[26px]">
          <div className="mb-3 flex items-center gap-3.5">
            <Avatar
              src={`${process.env.PUBLIC_URL}/hero-portrait.jpg`}
              alt="Dennis Valchez"
              size="xs"
            />
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="text-small font-semibold uppercase leading-none tracking-eyebrow text-primary">
                {text(content, 'brand.name') || BRAND_NAME}
              </span>
              <span className="text-[10px] font-medium uppercase leading-none tracking-[0.22em] text-primary/50">
                {fmtReplyAgo(r.date, r.reply.date)}
              </span>
              <span className="text-[10px] font-medium uppercase leading-none tracking-[0.22em] text-primary/50 [font-variant-numeric:tabular-nums] sm:hidden">
                {fmtDate(r.reply.date)}
              </span>
            </div>
            <span className="hidden text-[10px] font-medium uppercase tracking-eyebrow text-primary/50 [font-variant-numeric:tabular-nums] sm:block">
              {fmtDate(r.reply.date)}
            </span>
          </div>
          <p className="m-0 text-[14px] leading-[1.65] text-primary/80 [text-wrap:pretty]">
            {r.reply.body}
          </p>
        </div>
      ) : null}
    </article>
  );
}

export function ReviewCard(props: Props) {
  if (props.variant === 'detailed') {
    return <DetailedCard review={props.review} />;
  }
  const { name, role, body, rating, avatarInitial } = props;
  return (
    <CompactCard
      name={name}
      role={role}
      body={body}
      rating={rating}
      avatarInitial={avatarInitial}
    />
  );
}
